import { Component, OnInit, OnDestroy } from '@angular/core';
import { Site } from '@shared/models/site';
import { SortSummary, SortSummaryItem } from '../../../../models/shift-validation/sort-summary';
import { ProductService } from '../../../../services/product.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { ShiftValidationService } from '../../../../services/shift-validation/shift-validation.service';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { SubinventoryType } from '@shared/models/subinventory';
import { FormControl } from '@angular/forms';
import { getSiteInfo } from '@shared/helpers/site-info';
import { url } from '@shared/helpers/url';
import { Product } from '@shared/models/product';
import { SiteConfig } from '@shared/models/site-config';

@Component({
    selector: 'app-storage',
    templateUrl: './sort-summary.component.html',
    styleUrls: ['./sort-summary.component.scss']
})
export class SortSummaryComponent implements OnInit, OnDestroy {

    sortSummary = new BehaviorSubject<SortSummary>({ items: [] });

    showZeroRows = new FormControl<boolean>(false);

    filteredRows = combineLatest([
        this.sortSummary,
        this.productService.CurrentProducts,
        this.showZeroRows.valueChanges.pipe(startWith(this.showZeroRows.value))
    ]).pipe(map(([summary, products, showZeroRows]) => {
        let items = summary.items;

        if (!showZeroRows)
            items = items.filter(i => !this.allAreZero(i));

        const prodName = (productId: number) => products.find(p => p.id === productId)?.label ?? 'zzzzz';

        return items.sort((a, b) => prodName(a.productId).localeCompare(prodName(b.productId)));
    }));

    products = new BehaviorSubject<Product[]>([]);

    loading = false;
    site: Site;
    subscriptions: Subscription[];
    startupSubscription: Subscription;
    subinventoryType: SubinventoryType;
    activeRoute: string;

    constructor(
        private shiftContextService: ShiftContextService,
        private shiftValidationService: ShiftValidationService,
        private productService: ProductService,
        private route: ActivatedRoute) { }

    ngOnInit(): void {
        this.loading = true;

        this.subscriptions = [
            this.productService.AllProducts
                .subscribe(products => this.products.next(products)),
            combineLatest([this.route.params, this.shiftContextService.CurrentContext])
                .subscribe(([routeParams, context]) => {
                    const subinventoryId = parseInt(routeParams.id, 10);
                    this.site = context.site;

                    this.subinventoryType = this.site.subinventories.find(s => s.id === subinventoryId)?.subinventoryType;

                    const summary = this.shiftValidationService.CurrentMappedSummary<SortSummary>(url`Summaries/${subinventoryId}`);

                    this.sortSummary.next(summary);

                    this.loading = false;
                })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.sortSummary.next({ items: [] });
        this.products.next([]);
    }

    allAreZero(obj: SortSummaryItem): boolean {
        const values = [
            0,
            obj.startingFloorInventory,
            obj.startingTrailerInventory,
            obj.startingPreloadedLoads,
            obj.startingPreloadedPallets,
            obj.receivedLoads,
            obj.receivedUnits,
            obj.shippedLoads,
            obj.shippedUnits,
            obj.netReclass,
            obj.internalTransfersIn,
            obj.internalTransfersOut,
            obj.sortedIn,
            obj.endingFloorInventory,
            obj.endingTrailerInventory,
            obj.endingPreloadedLoads,
            obj.endingPreloadedPallets,
            obj.endingBalance,
            obj.weeklyVariance
        ];
        return new Set(values.map(v => v ?? 0)).size === 1;
    }

    getProduct(productId: number): Observable<string> {
        return this.products.pipe(map(p => p.find(product => product.id === productId)?.label));
    }

    doesTransfers(): Observable<boolean> {
        return this.shiftContextService.SiteConfig.pipe(map(configs => {
            return configs
                .filter(c => [SiteConfig.SITE_RAD_TRANS_ENABLE, SiteConfig.SITE_VOP_TRANS_ENABLE, SiteConfig.SITE_PECO_TRANS_ENABLE, SiteConfig.SITE_RM2_TRANS_ENABLE].includes(c.configKey))
                .some(c => c?.GetValue<boolean>()) ?? false;
        }));
    }

    getSortedQuantity(row: SortSummaryItem): number {
        return row.sortedOut.reduce((sum, current) => sum + current.quantity, 0);
    }

    getBeginningInventoryTooltip(row: SortSummaryItem): string {
        if (getSiteInfo(this.site).isTpm)
            return `Floor: ${row.startingFloorInventory}\nPartial Trailers: ${row.startingTrailerInventory}\nPreloadeded: ${row.startingPreloadedPallets} on ${row.startingPreloadedLoads} loads`;
        else
            return `Counted: ${row.startingFloorInventory + row.startingTrailerInventory}\nPreloadeded: ${row.startingPreloadedPallets} on ${row.startingPreloadedLoads} loads`;
    }

    getTransfersTotal(row: SortSummaryItem): number {
        return row.internalTransfersIn - row.internalTransfersOut;
    }

    getShippedTotal(row: SortSummaryItem): number {
        return row.shippedUnits + row.reuseUnits;
    }

    getShippedTooltip(row: SortSummaryItem): string {
        return `Shipped: ${row.shippedUnits} on ${row.shippedLoads} loads\nReuse: ${row.reuseUnits} on ${row.reuseLoads} loads`;
    }

    getBeginningInventory(row: SortSummaryItem): number {
        return row.startingFloorInventory + row.startingTrailerInventory + row.startingPreloadedPallets;
    }

    getEndingInventory(row: SortSummaryItem): number {
        return row.endingFloorInventory + row.endingTrailerInventory + row.endingPreloadedPallets;
    }

    getSortedInQuantityTooltip(row: SortSummaryItem): Observable<string> {
        return this.products.pipe(map(products => {
            return row.sortedInDetail.reduce((str, current) => (str ? str + '\n' : '')
                + `${current.quantity} - ${products.find(p => p.id === current.productId)?.label}`, '');
        }));
    }

    getSortedQuantityTooltip(row: SortSummaryItem): Observable<string> {
        return this.products.pipe(map(products => {
            return row.sortedOut.reduce((str, current) => (str ? str + '\n' : '')
                + `${current.quantity} - ${products.find(p => p.id === current.productId)?.label ?? (row.sortOutIsScrap ? 'Scrap' : 'Teardown')}`, '');
        }));
    }

    getReclassTooltip(row: SortSummaryItem): Observable<string> {
        return this.products.pipe(map(products => {
            return row.reclasses
                .slice()
                .sort((l, r) => l.quantity - r.quantity)
                .reduce((agg, item) => agg
                    + (agg ? '\n' : '')
                    + (item.quantity > 0 ? '+' : '')
                    + (item.quantity)
                    + ' - '
                    + products.find(p => p.id === item.productId)?.label, '');
        }));
    }

    getEndingInventoryTooltip(row: SortSummaryItem): string {
        if (getSiteInfo(this.site).isTpm)
            return `Floor: ${row.endingFloorInventory}\nPartial Trailers: ${row.endingTrailerInventory}\nPreloadeded: ${row.endingPreloadedPallets} on ${row.endingPreloadedLoads} loads`;
        else
            return `Counted: ${row.endingFloorInventory + row.endingTrailerInventory}\nPreloadeded: ${row.endingPreloadedPallets} on ${row.endingPreloadedLoads} loads`;
    }

    getInternalTransfersTooltip(row: SortSummaryItem): string {
        return `In: ${row.internalTransfersIn}\nOut: ${row.internalTransfersOut}`;
    }

    getBalanceTooltip(row: SortSummaryItem): string {
        return `Starting Balance: ${row.startingBalance}`;
    }

    sumTotal(selector: string | ((item: SortSummaryItem) => number)): Observable<number> {
        const func = typeof selector === 'string'
            ? (row: SortSummaryItem) => <number>row[selector]
            : selector;

        return this.sortSummary.pipe(map(summary => {
            return (summary.items ?? [])
                .map(func)
                .reduce((agg, cur) => agg + cur, 0);
        }));
    }
}
